/**
 * SEO Sprint 4B (Feb 2026) — Veelgevraagde Caribbean-vragen aggregator.
 *
 * Bouwt een sectie met 5 destination-cards (Curaçao, Aruba, Bonaire,
 * Sint Maarten, Suriname) waarbij elke card de TOP-3 FAQ-vragen toont
 * uit de bijbehorende destination CMS-data. Géén content-duplicatie:
 * de volledige antwoorden staan op /shipping-to-{slug}.
 *
 * Single source of truth = destination_page_settings (Sprint 2+3+4).
 *
 * Cards = teaser (vraag + link "Bekijk alle vragen over X →").
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { DESTINATION_LINKS } from "../RelatedDestinations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CaribbeanFAQAggregator({ testid = "caribbean-faq-aggregator" }) {
  const [byslug, setByslug] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      DESTINATION_LINKS.map((d) =>
        axios.get(`${BACKEND_URL}/api/website/destinations/${d.slug}`, { timeout: 8000 })
          .then((r) => ({ slug: d.slug, sections: r?.data?.sections }))
          .catch(() => ({ slug: d.slug, sections: null }))
      ),
    ).then((results) => {
      if (cancelled) return;
      const map = {};
      for (const r of results) {
        const items = Array.isArray(r.sections?.faq?.items) ? r.sections.faq.items : [];
        map[r.slug] = items.slice(0, 3).filter((q) => q?.question);
      }
      setByslug(map);
    }).finally(() => {
      if (!cancelled) setLoaded(true);
    });
    return () => { cancelled = true; };
  }, []);

  if (!loaded) {
    return (
      <div className="py-10 text-center text-gray-400 text-sm" data-testid={`${testid}-loading`}>
        Caribbean-vragen laden...
      </div>
    );
  }

  return (
    <section
      className="bg-[#F8FAFC] border-t border-slate-100 py-14 sm:py-16"
      data-testid={testid}
      id="caribbean-faq"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-10">
          <span className="text-[11px] uppercase tracking-[0.2em] text-[#1a5193] font-bold">
            Per bestemming
          </span>
          <h2 className="font-chivo font-bold text-2xl sm:text-3xl text-[#082c50] tracking-tight mt-3">
            Veelgevraagde Caribbean-vragen
          </h2>
          <p className="mt-3 text-[#514e4e] leading-relaxed">
            De meest gestelde vragen over pakketten, zeevracht, luchtvracht,
            documenten en betalen per bestemming. Voor de volledige
            antwoorden bekijkt u de betreffende destination-pagina.
          </p>
        </div>
        <div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
          data-testid={`${testid}-list`}
        >
          {DESTINATION_LINKS.map((dest) => {
            const items = byslug[dest.slug] || [];
            return (
              <article
                key={dest.slug}
                className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 flex flex-col"
                data-testid={`${testid}-card-${dest.slug}`}
              >
                <div className="flex items-center gap-2.5 mb-4">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-[#082c50] via-[#1a5193] to-[#194d8d] text-white">
                    <MapPin className="w-4 h-4" strokeWidth={2} aria-hidden="true" />
                  </span>
                  <h3 className="font-bold text-[#082c50] tracking-tight">
                    Verzending naar {dest.name}
                  </h3>
                </div>
                {items.length > 0 ? (
                  <ul
                    className="space-y-2 text-sm text-[#514e4e] mb-5 flex-1"
                    data-testid={`${testid}-card-${dest.slug}-questions`}
                  >
                    {items.map((q, i) => (
                      <li key={i} className="flex items-start gap-2 leading-snug">
                        <span className="text-[#1a5193] font-bold mt-0.5">•</span>
                        <span>{q.question}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-400 italic mb-5 flex-1">
                    Vragen worden geladen…
                  </p>
                )}
                <Link
                  to={dest.url}
                  className="group inline-flex items-center justify-between gap-2 mt-auto pt-3 border-t border-slate-100 text-[11px] uppercase tracking-[0.16em] font-bold text-[#1a5193] hover:text-[#082c50] transition-colors"
                  data-testid={`${testid}-link-${dest.slug}`}
                >
                  <span>Bekijk alle vragen over verzending naar {dest.name}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                </Link>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
